import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getChatbotAdminSummary } from '../services/adminApi';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getChatbotAdminSummary()
      .then((summary) => {
        setStats({
          totalCats: summary.totalCategories ?? 0,
          activeCats: summary.activeCategories ?? 0,
          totalQs: summary.totalQuestions ?? 0,
          activeQs: summary.activeQuestions ?? 0,
        });
      })
      .catch(() => setStats({ totalCats: 0, activeCats: 0, totalQs: 0, activeQs: 0 }))
      .finally(() => setLoading(false));
  }, []);

  const STAT_CARDS = stats
    ? [
        {
          label: 'Total Categories',
          value: stats.totalCats,
          variant: 'primary',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
              <path d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
            </svg>
          ),
        },
        {
          label: 'Active Categories',
          value: stats.activeCats,
          variant: 'success',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          ),
        },
        {
          label: 'Total Questions',
          value: stats.totalQs,
          variant: 'info',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          ),
        },
        {
          label: 'Active Questions',
          value: stats.activeQs,
          variant: 'warn',
          icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
              <polyline points="9 11 12 14 22 4"/>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
            </svg>
          ),
        },
      ]
    : [];

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <div>
          <h1 className="admin-page__title">Dashboard</h1>
          <p className="admin-page__sub">Overview of your PharmVetPat chatbot knowledge base</p>
        </div>
      </div>

      {loading ? (
        <div className="admin-loading">
          <span className="admin-spinner admin-spinner--dark" />
          Loading stats…
        </div>
      ) : (
        <div className="admin-stats">
          {STAT_CARDS.map((card) => (
            <div className="admin-stat" key={card.label}>
              <div className={`admin-stat__icon admin-stat__icon--${card.variant}`}>
                {card.icon}
              </div>
              <div>
                <div className="admin-stat__value">{card.value}</div>
                <div className="admin-stat__label">{card.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="admin-quick">
        <button type="button" className="admin-quick__card" onClick={() => navigate('/admin/categories')}>
          <span className="admin-quick__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
              <path d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
            </svg>
          </span>
          Manage Categories
          <span className="admin-quick__desc">Add, edit, activate categories</span>
        </button>

        <button type="button" className="admin-quick__card" onClick={() => navigate('/admin/questions')}>
          <span className="admin-quick__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </span>
          Manage Questions
          <span className="admin-quick__desc">Create, configure, preview questions</span>
        </button>

        <button type="button" className="admin-quick__card" onClick={() => navigate('/admin/settings')}>
          <span className="admin-quick__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </span>
          Settings
          <span className="admin-quick__desc">View configuration &amp; profile</span>
        </button>
      </div>
    </div>
  );
}
